import { coerceDate } from './date-formatters';

const EPOCH = new Date(0);

export type DateCoercible =
  | Date
  | string
  | number
  | null
  | undefined
  | { toISOString?: () => string; getTime?: () => number };

const isDateObject = (candidate: unknown): candidate is Date =>
  Object.prototype.toString.call(candidate) === '[object Date]';

type IsoSerializable = { toISOString: () => string };
type EpochSerializable = { getTime: () => number };

const hasToISOString = (candidate: unknown): candidate is IsoSerializable =>
  typeof candidate === 'object' &&
  candidate !== null &&
  typeof (candidate as Partial<IsoSerializable>).toISOString === 'function';

const hasGetTime = (candidate: unknown): candidate is EpochSerializable =>
  typeof candidate === 'object' &&
  candidate !== null &&
  typeof (candidate as Partial<EpochSerializable>).getTime === 'function';

function normalizeDateInput(value: DateCoercible): Date | string | number | null | undefined {
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }
  if (isDateObject(value)) {
    return value;
  }
  if (hasToISOString(value)) {
    return value.toISOString();
  }
  if (hasGetTime(value)) {
    return value.getTime();
  }
  return null;
}

/**
 * Converts an unknown value into a valid Date.
 * Falls back to the provided default (epoch by default) when parsing fails.
 */
export function ensureDate(value: DateCoercible, fallback: Date = EPOCH): Date {
  const parsed = ensureDateOrNull(value);
  return parsed ?? fallback;
}

export function ensureDateOrNull(value: DateCoercible): Date | null {
  const normalized = normalizeDateInput(value);
  return coerceDate(normalized);
}

export function ensureDateOrUndefined(value: DateCoercible): Date | undefined {
  const parsed = ensureDateOrNull(value);
  return parsed ?? undefined;
}

/**
 * Ensures a valid ISO string by coercing the input to a Date first.
 * Falls back to the provided Date (or `new Date()`) when parsing fails.
 */
export function ensureIsoDateString(value: DateCoercible, fallback: Date = new Date()): string {
  return ensureDate(value, fallback).toISOString();
}

export const isDateCoercible = (value: unknown): value is DateCoercible => {
  if (value === null || value === undefined) {
    return true;
  }
  if (isDateObject(value)) {
    return true;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return true;
  }
  if (typeof value === 'object') {
    return hasToISOString(value) || hasGetTime(value);
  }
  return false;
};
